import { useState, useEffect, useRef, useCallback } from "react";
import { useAuth } from "../../App";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { ScrollArea } from "../../components/ui/scroll-area";
import { Progress } from "../../components/ui/progress";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "../../components/ui/sheet";
import { 
  MessageCircle, Send, Loader2, Bot, User, Sparkles, 
  Clock, Trash2, Plus, ChevronRight, Building2, HelpCircle,
  BookOpen, Scale, FileText, Users, Euro, RefreshCw, 
  AlertTriangle, CheckCircle2, ExternalLink, Zap, ArrowRight,
  Calendar, TrendingUp, Target, FileCheck, X, Download, Eye
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

export default function ChatbotJuridiqueEnhanced({ onNavigate }) {
  const { token } = useAuth();
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [message, setMessage] = useState("");
  const [messages, setMessages] = useState([]);
  const [sessionId, setSessionId] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [companyStatus, setCompanyStatus] = useState(null);
  const [showSuggestions, setShowSuggestions] = useState(true);
  
  // Document viewer state
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [documentLoading, setDocumentLoading] = useState(false);
  const [documentData, setDocumentData] = useState(null);
  
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Fetch initial data including compliance status
  const fetchInitialData = useCallback(async () => {
    setLoading(true);
    try {
      const [suggestionsRes, statusRes, sessionsRes] = await Promise.all([
        axios.get(`${API}/api/corporate/chatbot/suggestions`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/api/corporate/chatbot/status`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/api/corporate/chatbot/sessions`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      
      setSuggestions(suggestionsRes.data.suggestions || []);
      setCompanyStatus(statusRes.data);
      setSessions(sessionsRes.data.sessions || []);
    } catch (error) {
      console.error("Error fetching initial data:", error);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchInitialData();
  }, [fetchInitialData]);

  // Load session
  const loadSession = async (sid) => {
    try {
      const res = await axios.get(`${API}/api/corporate/chatbot/sessions/${sid}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSessionId(sid);
      setMessages(res.data.messages || []);
      setShowSuggestions(false);
    } catch (error) {
      toast.error("Erreur lors du chargement");
    }
  };

  // Start new conversation
  const startNewConversation = () => {
    setSessionId(null);
    setMessages([]);
    setShowSuggestions(true);
  };

  // Send message
  const sendMessage = async (text = message) => {
    if (!text.trim()) return;
    
    setSending(true);
    setShowSuggestions(false);
    
    const userMessage = {
      role: "user",
      content: text,
      timestamp: new Date().toISOString()
    };
    setMessages(prev => [...prev, userMessage]);
    setMessage("");
    
    try {
      const res = await axios.post(`${API}/api/corporate/chatbot/chat`, {
        message: text,
        session_id: sessionId
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.data.success) {
        if (!sessionId) {
          setSessionId(res.data.session_id);
          const sessionsRes = await axios.get(`${API}/api/corporate/chatbot/sessions`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          setSessions(sessionsRes.data.sessions || []);
        }
        
        const assistantMessage = {
          role: "assistant",
          content: res.data.response,
          timestamp: res.data.timestamp,
          document_references: res.data.document_references,
          suggested_actions: res.data.suggested_actions
        };
        setMessages(prev => [...prev, assistantMessage]);
      } else {
        const errorMsg = res.data.error || "Erreur";
        if (errorMsg.includes("Budget") || errorMsg.includes("budget")) {
          toast.error("Budget LLM epuise. Rechargez dans Profil > Universal Key.");
        } else {
          toast.error(errorMsg);
        }
        setMessages(prev => prev.slice(0, -1));
      }
    } catch (error) {
      toast.error("Erreur de communication");
      setMessages(prev => prev.slice(0, -1));
    } finally {
      setSending(false);
      inputRef.current?.focus();
    }
  };

  // Delete session
  const deleteSession = async (sid, e) => {
    e.stopPropagation();
    try {
      await axios.delete(`${API}/api/corporate/chatbot/sessions/${sid}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSessions(prev => prev.filter(s => s.session_id !== sid));
      if (sessionId === sid) startNewConversation();
      toast.success("Conversation supprimee");
    } catch (error) {
      toast.error("Erreur");
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  // Handle action clicks
  const handleAction = (action) => {
    if (onNavigate) {
      switch (action.type) {
        case 'generate_ago':
          onNavigate('ago');
          break;
        case 'go_profile':
          onNavigate('profil');
          break;
        case 'go_import':
        case 'import_statuts':
        case 'import_bilan':
          onNavigate('import');
          break;
        case 'go_registers':
        case 'view_registers':
          onNavigate('registres');
          break;
        case 'view_documents':
          onNavigate('coffre');
          break;
        default:
          break;
      }
    }
  };

  // View document from chatbot reference
  const handleViewDocument = async (doc) => {
    setSelectedDocument(doc);
    setDocumentLoading(true);
    setDocumentData(null);
    
    try {
      const res = await axios.get(`${API}/api/corporate/ocr/preview/${doc.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDocumentData(res.data);
    } catch (error) {
      console.error("Error fetching document:", error);
      toast.error("Erreur lors du chargement du document");
      setSelectedDocument(null);
    } finally {
      setDocumentLoading(false);
    }
  };

  // Close document viewer
  const closeDocumentViewer = () => {
    setSelectedDocument(null);
    setDocumentData(null);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  return (
    <div className="flex h-[calc(100vh-16rem)] gap-4">
      {/* Sidebar */}
      <div className="w-72 flex-shrink-0 hidden lg:flex flex-col gap-4">
        {/* Compliance Score Card */}
        {companyStatus && (
          <Card className="bg-gradient-to-br from-indigo-50 to-purple-50 border-indigo-200">
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-3">
                <span className="text-sm font-medium text-indigo-900">Score de conformite</span>
                <Badge className={`${
                  companyStatus.compliance_score >= 80 ? 'bg-green-500' :
                  companyStatus.compliance_score >= 50 ? 'bg-amber-500' : 'bg-red-500'
                }`}>
                  {companyStatus.compliance_score}%
                </Badge>
              </div>
              <Progress value={companyStatus.compliance_score} className="h-2 mb-3" />
              
              {/* Upcoming deadlines */}
              {companyStatus.upcoming_deadlines?.length > 0 && (
                <div className="mt-3 pt-3 border-t border-indigo-200">
                  {companyStatus.upcoming_deadlines.map((d, i) => (
                    <div key={i} className={`flex items-center gap-2 text-xs ${
                      d.status === 'urgent' ? 'text-red-600' :
                      d.status === 'warning' ? 'text-amber-600' : 'text-green-600'
                    }`}>
                      <Calendar className="w-3 h-3" />
                      <span>{d.label}: <strong>{d.days_until}j</strong></span>
                    </div>
                  ))}
                </div>
              )}
              
              {/* Missing items */}
              {companyStatus.missing_items?.length > 0 && (
                <div className="mt-3 pt-3 border-t border-indigo-200">
                  <p className="text-xs text-indigo-700 mb-2">A completer :</p>
                  {companyStatus.missing_items.slice(0, 3).map((item, i) => (
                    <button
                      key={i}
                      onClick={() => handleAction({ type: item.action })}
                      className="flex items-center gap-1 text-xs text-indigo-600 hover:text-indigo-800 mb-1"
                    >
                      <ChevronRight className="w-3 h-3" />
                      {item.message}
                    </button>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Sessions */}
        <Card className="flex-1">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm font-medium">Conversations</CardTitle>
              <Button variant="ghost" size="icon" onClick={startNewConversation} className="h-8 w-8">
                <Plus className="w-4 h-4" />
              </Button>
            </div>
          </CardHeader>
          <CardContent className="flex-1 overflow-hidden p-0">
            <ScrollArea className="h-[250px] px-4 pb-4">
              {sessions.length === 0 ? (
                <p className="text-sm text-gray-500 text-center py-4">Aucune conversation</p>
              ) : (
                <div className="space-y-2">
                  {sessions.map((session) => (
                    <div
                      key={session.session_id}
                      onClick={() => loadSession(session.session_id)}
                      className={`group p-3 rounded-lg cursor-pointer transition-colors ${
                        sessionId === session.session_id
                          ? "bg-indigo-50 border border-indigo-200"
                          : "hover:bg-gray-50 border border-transparent"
                      }`}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-900 truncate">
                            {session.preview?.substring(0, 40) || "Nouvelle conversation"}...
                          </p>
                          <p className="text-xs text-gray-500 mt-1 flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {new Date(session.updated_at).toLocaleDateString('fr-FR', { timeZone: 'Europe/Paris' })}
                          </p>
                        </div>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-6 w-6 opacity-0 group-hover:opacity-100"
                          onClick={(e) => deleteSession(session.session_id, e)}
                        >
                          <Trash2 className="w-3 h-3 text-gray-400 hover:text-red-500" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </ScrollArea>
          </CardContent>
        </Card>
      </div>

      {/* Main Chat Area */}
      <div className="flex-1 flex flex-col">
        <Card className="flex-1 flex flex-col overflow-hidden">
          {/* Header */}
          <CardHeader className="border-b py-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-lg">
                  <Bot className="w-5 h-5 text-white" />
                </div>
                <div>
                  <CardTitle className="text-base">Assistant Juridique IA</CardTitle>
                  <CardDescription className="text-xs">
                    Expert droit des societes • Reponses personnalisees
                  </CardDescription>
                </div>
              </div>
              
              {companyStatus?.compliance_score !== undefined && (
                <div className="flex items-center gap-2">
                  <div className={`px-3 py-1 rounded-full text-xs font-medium ${
                    companyStatus.compliance_score >= 80 ? 'bg-green-100 text-green-700' :
                    companyStatus.compliance_score >= 50 ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'
                  }`}>
                    <Target className="w-3 h-3 inline mr-1" />
                    {companyStatus.compliance_score}% conforme
                  </div>
                </div>
              )}
            </div>
          </CardHeader>

          {/* Messages Area */}
          <CardContent className="flex-1 overflow-hidden p-0">
            <ScrollArea className="h-full p-4">
              {/* Welcome / Suggestions */}
              {showSuggestions && messages.length === 0 && (
                <WelcomeScreen 
                  suggestions={suggestions}
                  companyStatus={companyStatus}
                  onSendMessage={sendMessage}
                  onAction={handleAction}
                  sending={sending}
                />
              )}

              {/* Chat Messages */}
              {messages.length > 0 && (
                <div className="space-y-4">
                  {messages.map((msg, idx) => (
                    <ChatMessage 
                      key={idx} 
                      message={msg} 
                      onAction={handleAction}
                      onViewDocument={handleViewDocument}
                    />
                  ))}
                  
                  {sending && <TypingIndicator />}
                  
                  <div ref={messagesEndRef} />
                </div>
              )}
            </ScrollArea>
          </CardContent>

          {/* Input Area */}
          <div className="border-t p-4">
            <div className="flex gap-2">
              <Input
                ref={inputRef}
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Posez votre question juridique..."
                disabled={sending}
                className="flex-1"
                data-testid="chat-input"
              />
              <Button
                onClick={() => sendMessage()}
                disabled={sending || !message.trim()}
                className="gap-2 bg-indigo-600 hover:bg-indigo-700"
                data-testid="send-button"
              >
                {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              </Button>
            </div>
            <p className="text-xs text-gray-400 mt-2 text-center">
              Reponses basees sur vos documents et le contexte de votre societe
            </p>
          </div>
        </Card>
      </div>

      {/* Document Viewer Sheet */}
      <Sheet open={!!selectedDocument} onOpenChange={(open) => !open && closeDocumentViewer()}>
        <SheetContent className="w-[500px] sm:w-[600px] overflow-y-auto">
          <SheetHeader>
            <SheetTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-indigo-600" />
              {selectedDocument?.name || "Document"}
            </SheetTitle>
          </SheetHeader>
          
          <div className="mt-6">
            {documentLoading ? (
              <div className="flex items-center justify-center h-64">
                <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
                <span className="ml-2 text-gray-500">Chargement...</span>
              </div>
            ) : documentData ? (
              <div className="space-y-4">
                {/* Document Info */}
                <div className="grid grid-cols-2 gap-3 p-4 bg-gray-50 rounded-lg">
                  <div>
                    <p className="text-xs text-gray-500">Type</p>
                    <p className="font-medium capitalize">{documentData.document_type}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Confiance OCR</p>
                    <Badge variant={documentData.classification_confidence > 0.8 ? "default" : "secondary"}>
                      {Math.round((documentData.classification_confidence || 0) * 100)}%
                    </Badge>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Statut</p>
                    <Badge variant={documentData.applied ? "default" : "outline"} className={documentData.applied ? "bg-green-500" : ""}>
                      {documentData.applied ? "Appliqué" : "En attente"}
                    </Badge>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Date import</p>
                    <p className="text-sm">{documentData.created_at ? new Date(documentData.created_at).toLocaleDateString('fr-FR', { timeZone: 'Europe/Paris' }) : '-'}</p>
                  </div>
                </div>

                {/* Extracted Data Preview */}
                {documentData.profile_data && (
                  <div className="p-4 border rounded-lg">
                    <h4 className="font-medium mb-3 flex items-center gap-2">
                      <CheckCircle2 className="w-4 h-4 text-green-500" />
                      Données extraites
                    </h4>
                    <div className="space-y-2 text-sm">
                      {Object.entries(documentData.profile_data).slice(0, 8).map(([key, value]) => (
                        value && typeof value !== 'object' && (
                          <div key={key} className="flex justify-between">
                            <span className="text-gray-500 capitalize">{key.replace(/_/g, ' ')}</span>
                            <span className="font-medium">{String(value).substring(0, 30)}{String(value).length > 30 ? '...' : ''}</span>
                          </div>
                        )
                      ))}
                    </div>
                  </div>
                )}

                {/* Actions */}
                <div className="flex gap-2 pt-4">
                  <Button
                    variant="outline"
                    className="flex-1 gap-2"
                    onClick={() => {
                      closeDocumentViewer();
                      onNavigate('coffre');
                    }}
                  >
                    <Eye className="w-4 h-4" />
                    Voir dans le Coffre
                  </Button>
                  <Button
                    variant="outline"
                    className="gap-2"
                    onClick={closeDocumentViewer}
                  >
                    <X className="w-4 h-4" />
                    Fermer
                  </Button>
                </div>
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                <FileText className="w-12 h-12 mx-auto text-gray-300 mb-2" />
                <p>Document non disponible</p>
              </div>
            )}
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
}

// Welcome Screen Component
function WelcomeScreen({ suggestions, companyStatus, onSendMessage, onAction, sending }) {
  return (
    <div className="space-y-6">
      <div className="text-center py-6">
        <div className="inline-flex p-4 bg-gradient-to-br from-indigo-100 to-purple-100 rounded-full mb-4">
          <Sparkles className="w-8 h-8 text-indigo-600" />
        </div>
        <h3 className="text-lg font-semibold text-gray-900">
          Assistant Juridique Intelligent
        </h3>
        <p className="text-gray-500 mt-2 max-w-md mx-auto">
          Je connais votre societe, vos documents et vos echeances.
          Posez-moi vos questions !
        </p>
      </div>

      {/* Quick Actions based on status */}
      {companyStatus?.suggestions?.length > 0 && (
        <div className="max-w-2xl mx-auto">
          <h4 className="text-sm font-medium text-gray-700 mb-3 flex items-center gap-2">
            <Zap className="w-4 h-4 text-amber-500" />
            Actions recommandees
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {companyStatus.suggestions.map((sug, idx) => (
              <button
                key={idx}
                onClick={() => onAction({ type: sug.action })}
                className="flex items-center gap-3 p-3 bg-amber-50 hover:bg-amber-100 border border-amber-200 rounded-lg text-left transition-colors"
              >
                <ArrowRight className="w-4 h-4 text-amber-600" />
                <span className="text-sm text-amber-800">{sug.message}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Suggested Questions */}
      <div className="max-w-2xl mx-auto">
        <h4 className="text-sm font-medium text-gray-700 mb-3">
          Questions frequentes
        </h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {suggestions.map((category, idx) => (
            <Card key={idx} className="p-3 hover:shadow-md transition-shadow">
              <div className="flex items-center gap-2 mb-2">
                <CategoryIcon category={category.category} />
                <span className="text-sm font-medium">{category.category}</span>
              </div>
              <div className="space-y-1">
                {category.questions.slice(0, 2).map((q, qIdx) => (
                  <button
                    key={qIdx}
                    onClick={() => onSendMessage(q)}
                    className="w-full text-left text-sm text-gray-600 hover:text-indigo-600 hover:bg-indigo-50 p-2 rounded transition-colors flex items-center gap-2"
                    disabled={sending}
                  >
                    <ChevronRight className="w-3 h-3 flex-shrink-0" />
                    <span className="truncate">{q}</span>
                  </button>
                ))}
              </div>
            </Card>
          ))}
        </div>
      </div>
    </div>
  );
}

// Chat Message Component with Markdown rendering
function ChatMessage({ message, onAction, onViewDocument }) {
  const isUser = message.role === "user";
  
  return (
    <div className={`flex gap-3 ${isUser ? "justify-end" : "justify-start"}`}>
      {!isUser && (
        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center flex-shrink-0">
          <Bot className="w-4 h-4 text-white" />
        </div>
      )}
      
      <div className={`max-w-[85%] ${isUser ? "order-first" : ""}`}>
        <div className={`rounded-2xl px-4 py-3 ${
          isUser ? "bg-indigo-600 text-white" : "bg-gray-50 border border-gray-100"
        }`}>
          {isUser ? (
            <p className="text-sm">{message.content}</p>
          ) : (
            <FormattedResponse 
              content={message.content} 
              onAction={onAction}
            />
          )}
        </div>
        
        {/* Document References - Clickable */}
        {!isUser && message.document_references?.length > 0 && (
          <div className="mt-2">
            <p className="text-xs text-gray-500 mb-1">Documents cités :</p>
            <div className="flex flex-wrap gap-2">
              {message.document_references.map((doc, idx) => (
                <button
                  key={idx}
                  onClick={() => onViewDocument && onViewDocument(doc)}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-blue-50 text-blue-700 text-xs rounded-lg hover:bg-blue-100 border border-blue-200 transition-colors"
                  title={`Voir le document: ${doc.name}`}
                >
                  <FileText className="w-3.5 h-3.5" />
                  <span className="max-w-[150px] truncate">{doc.name}</span>
                  <ExternalLink className="w-3 h-3" />
                </button>
              ))}
            </div>
          </div>
        )}
        
        {/* Suggested Actions */}
        {!isUser && message.suggested_actions?.length > 0 && (
          <div className="mt-2 flex flex-wrap gap-2">
            {message.suggested_actions.map((action, idx) => (
              <Button
                key={idx}
                variant="outline"
                size="sm"
                className="h-7 text-xs gap-1 border-indigo-200 text-indigo-700 hover:bg-indigo-50"
                onClick={() => onAction(action)}
              >
                <Zap className="w-3 h-3" />
                {action.label}
              </Button>
            ))}
          </div>
        )}
        
        <p className={`text-xs mt-1 ${isUser ? "text-indigo-200 text-right" : "text-gray-400"}`}>
          {message.timestamp && new Date(message.timestamp).toLocaleTimeString('fr-FR', {
            hour: '2-digit',
            minute: '2-digit',
            timeZone: 'Europe/Paris'
          })}
        </p>
      </div>
      
      {isUser && (
        <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center flex-shrink-0">
          <User className="w-4 h-4 text-gray-600" />
        </div>
      )}
    </div>
  );
}

// Formatted Response with Markdown-like rendering
function FormattedResponse({ content, onAction }) {
  // Parse and render the content with formatting
  const renderContent = (text) => {
    // Split by lines for processing
    const lines = text.split('\n');
    const elements = [];
    let currentList = [];
    let listType = null;
    
    lines.forEach((line, idx) => {
      // Headers
      if (line.startsWith('## ')) {
        if (currentList.length > 0) {
          elements.push(<ul key={`list-${idx}`} className="list-disc pl-5 space-y-1 my-2">{currentList}</ul>);
          currentList = [];
        }
        elements.push(
          <h3 key={idx} className="text-base font-semibold text-gray-900 mt-3 mb-2 flex items-center gap-2">
            {renderInline(line.substring(3))}
          </h3>
        );
      }
      else if (line.startsWith('### ')) {
        if (currentList.length > 0) {
          elements.push(<ul key={`list-${idx}`} className="list-disc pl-5 space-y-1 my-2">{currentList}</ul>);
          currentList = [];
        }
        elements.push(
          <h4 key={idx} className="text-sm font-semibold text-gray-800 mt-2 mb-1">
            {renderInline(line.substring(4))}
          </h4>
        );
      }
      // List items
      else if (line.match(/^[\-\*]\s/)) {
        currentList.push(
          <li key={idx} className="text-sm text-gray-700">
            {renderInline(line.substring(2))}
          </li>
        );
      }
      else if (line.match(/^\d+\.\s/)) {
        currentList.push(
          <li key={idx} className="text-sm text-gray-700">
            {renderInline(line.replace(/^\d+\.\s/, ''))}
          </li>
        );
      }
      // Action buttons
      else if (line.includes('[[ACTION:')) {
        if (currentList.length > 0) {
          elements.push(<ul key={`list-${idx}`} className="list-disc pl-5 space-y-1 my-2">{currentList}</ul>);
          currentList = [];
        }
        const actionMatch = line.match(/\[\[ACTION:(\w+):([^\]]+)\]\]/);
        if (actionMatch) {
          elements.push(
            <Button
              key={idx}
              variant="outline"
              size="sm"
              className="mt-2 gap-1 border-indigo-200 text-indigo-700 hover:bg-indigo-50"
              onClick={() => onAction({ type: actionMatch[1], label: actionMatch[2] })}
            >
              <Zap className="w-3 h-3" />
              {actionMatch[2]}
            </Button>
          );
        }
      }
      // Document references (UUID can contain hyphens)
      else if (line.includes('[[DOC:')) {
        const docMatch = line.match(/\[\[DOC:(\w+):([\w-]+):([^\]]+)\]\]/);
        if (docMatch) {
          elements.push(
            <button
              key={idx}
              className="inline-flex items-center gap-1 px-2 py-1 bg-blue-50 text-blue-700 text-xs rounded-full hover:bg-blue-100 my-1"
            >
              <FileCheck className="w-3 h-3" />
              {docMatch[3]}
              <ExternalLink className="w-3 h-3" />
            </button>
          );
        }
      }
      // Horizontal rule
      else if (line.trim() === '---') {
        if (currentList.length > 0) {
          elements.push(<ul key={`list-${idx}`} className="list-disc pl-5 space-y-1 my-2">{currentList}</ul>);
          currentList = [];
        }
        elements.push(<hr key={idx} className="my-3 border-gray-200" />);
      }
      // Regular paragraph
      else if (line.trim()) {
        if (currentList.length > 0) {
          elements.push(<ul key={`list-${idx}`} className="list-disc pl-5 space-y-1 my-2">{currentList}</ul>);
          currentList = [];
        }
        elements.push(
          <p key={idx} className="text-sm text-gray-700 my-1">
            {renderInline(line)}
          </p>
        );
      }
    });
    
    if (currentList.length > 0) {
      elements.push(<ul key="list-end" className="list-disc pl-5 space-y-1 my-2">{currentList}</ul>);
    }
    
    return elements;
  };
  
  // Render inline formatting (bold, italic, code, emoji)
  const renderInline = (text) => {
    // First, remove [[ACTION:...]] and [[DOC:...]] tags from inline text
    // They are handled separately
    let cleanText = text
      .replace(/\[\[ACTION:\w+:[^\]]+\]\]/g, '')
      .replace(/\[\[DOC:\w+:[\w-]+:[^\]]+\]\]/g, '')
      .trim();
    
    if (!cleanText) return null;
    
    // Handle bold
    const parts = cleanText.split(/(\*\*[^*]+\*\*)/g);
    return parts.map((part, i) => {
      if (part.startsWith('**') && part.endsWith('**')) {
        return <strong key={i} className="font-semibold">{part.slice(2, -2)}</strong>;
      }
      // Handle inline code
      const codeParts = part.split(/(`[^`]+`)/g);
      return codeParts.map((codePart, j) => {
        if (codePart.startsWith('`') && codePart.endsWith('`')) {
          return <code key={`${i}-${j}`} className="px-1 py-0.5 bg-gray-100 rounded text-xs font-mono">{codePart.slice(1, -1)}</code>;
        }
        return codePart;
      });
    });
  };
  
  return <div className="prose-sm">{renderContent(content)}</div>;
}

// Typing Indicator
function TypingIndicator() {
  return (
    <div className="flex gap-3 justify-start">
      <div className="w-8 h-8 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center">
        <Bot className="w-4 h-4 text-white" />
      </div>
      <div className="bg-gray-50 border border-gray-100 rounded-2xl px-4 py-3">
        <div className="flex gap-1">
          <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: "0ms" }} />
          <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: "150ms" }} />
          <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: "300ms" }} />
        </div>
      </div>
    </div>
  );
}

// Category Icon Helper
function CategoryIcon({ category }) {
  const iconClass = "w-4 h-4";
  switch (category) {
    case "AGO / AGE": return <Scale className={iconClass + " text-purple-500"} />;
    case "Formalites": return <FileText className={iconClass + " text-blue-500"} />;
    case "Capital & Parts": return <Euro className={iconClass + " text-green-500"} />;
    case "Registres": return <BookOpen className={iconClass + " text-amber-500"} />;
    case "Dirigeants": return <Users className={iconClass + " text-indigo-500"} />;
    default: return <HelpCircle className={iconClass + " text-gray-500"} />;
  }
}
